import type { ListMoviesRequest, MovieCard as MovieCardModel } from '@zyx/shared';
import type { ReactNode } from 'react';
import { Clapperboard } from 'lucide-react';
import { EmptyState, ErrorState } from '@/components/common/states';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMovieList } from '@/hooks/use-movies';
import { MovieCard } from './movie-card';

const GRID = 'grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6';

export function MovieGridSkeleton(props: { readonly count?: number }) {
  return (
    <div className={GRID}>
      {Array.from({ length: props.count ?? 12 }, (_unused, index) => (
        <div key={index} className="space-y-2">
          <Skeleton className="aspect-[3/2] w-full rounded-lg" />
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-full" />
        </div>
      ))}
    </div>
  );
}

export interface CardMenuContext {
  readonly index: number;
  /** Every movie loaded so far, in order. */
  readonly loaded: ReadonlyArray<MovieCardModel>;
  /** Whether more pages follow the loaded ones. */
  readonly hasMore: boolean;
}

/** Movies for a request, with "load more" rather than numbered pages. `cardMenu` adds the page's own controls to each card. */
export function MovieGrid(props: {
  readonly request: Omit<ListMoviesRequest, 'page'>;
  readonly empty: { readonly title: string; readonly description?: string };
  readonly cardMenu?: (movie: MovieCardModel, context: CardMenuContext) => ReactNode;
}) {
  const query = useMovieList(props.request);

  if (query.isPending) {
    return <MovieGridSkeleton />;
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  const movies = query.data.pages.flatMap((page) => page.movies);
  if (movies.length === 0) {
    return <EmptyState title={props.empty.title} description={props.empty.description} icon={<Clapperboard className="size-5" />} />;
  }
  return (
    <div className="space-y-8">
      <div className={GRID}>
        {movies.map((movie, index) => (
          <MovieCard key={movie.movieId} movie={movie} menu={props.cardMenu?.(movie, { index, loaded: movies, hasMore: query.hasNextPage })} />
        ))}
      </div>
      {query.hasNextPage ? (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage}>
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export { GRID as MOVIE_GRID_CLASSES };
